import { motion } from 'motion/react'
import type { VolumeWeekOut } from '../../api/types'
import { useUnits } from '../../lib/UnitsProvider'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../ui/AsyncPanel'
import { TOKENS } from '../ui/dashboard-primitives'
import { useVolumePanel } from './panelStates'

const EASE = [0.16, 1, 0.3, 1] as const
/** The bars the grid below has columns for. */
const CHART_WEEKS = 7

function label(iso: string, showMonth: boolean): string {
  const d = new Date(`${iso}T00:00`)
  return showMonth
    ? d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
    : String(d.getDate())
}

/** The last seven weeks of `GET /api/volume`'s weekly series — every source,
 *  the same calculation as every other tonnage figure. The label stays up in
 *  every state; the bars go through `AsyncPanel` (Spec 2 D5, #170), so a failed
 *  request no longer draws as a blank chart. */
export function TonnageChart() {
  const volume = useVolumePanel()

  return (
    <div>
      <p
        className="text-[0.5625rem] tracking-[0.28em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
      >
        Tonnage · by week
      </p>
      <AsyncPanel
        sources={volume.sources}
        hasContent={volume.hasContent}
        subject="Your weekly tonnage"
        skeleton={
          <PanelSkeleton label="Loading weekly tonnage">
            <div className="mt-4">
              <Shimmer height={116} />
            </div>
            <div className="mt-3.5 w-56 max-w-full">
              <Shimmer height={12} />
            </div>
          </PanelSkeleton>
        }
      >
        {() => <TonnageBars weeks={(volume.data?.weekly ?? []).slice(-CHART_WEEKS)} />}
      </AsyncPanel>
    </div>
  )
}

function TonnageBars({ weeks }: { weeks: VolumeWeekOut[] }) {
  const { tonnage } = useUnits()
  const max = Math.max(1, ...weeks.map((w) => w.tonnage_kg))
  const peakIdx = weeks.reduce((best, w, i) => (w.tonnage_kg > weeks[best].tonnage_kg ? i : best), 0)
  const peak = weeks[peakIdx]

  return (
    <>
      <div className="grid grid-cols-7 gap-2.5 items-end mt-4" style={{ height: 116 }}>
        {weeks.map((w, i) => {
          const isNow = i === weeks.length - 1
          const isPeak = i === peakIdx && !isNow
          const ratio = w.tonnage_kg / max
          const color = isNow
            ? TOKENS.amber
            : isPeak
              ? TOKENS.creamDim
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              : 'rgba(245,237,225,0.28)'
          return (
            <div key={w.week_start} className="relative h-full flex items-end">
              <motion.i
                className="block w-full origin-bottom"
                style={{ height: '100%', background: color, borderRadius: '6px 6px 2px 2px' }}
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: Math.max(ratio, 0.02) }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: EASE, delay: i * 0.07 }}
              />
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-7 gap-2.5 mt-2.5">
        {weeks.map((w, i) => (
          <span
            key={w.week_start}
            className="text-[0.5625rem] text-center"
            style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
          >
            {label(w.week_start, i === 0 || i === 3)}
          </span>
        ))}
      </div>

      {peak && (
        <p className="mt-2 text-[0.8125rem]" style={{ color: TOKENS.creamSoft }}>
          Peak week moved {tonnage(peak.tonnage_kg)}. The current week sits at{' '}
          {tonnage(weeks[weeks.length - 1]?.tonnage_kg ?? 0)}.
        </p>
      )}
    </>
  )
}
